import gql from "graphql-tag";

import {ContactGroupItems} from "@/lib/queries/contacts.ts";
import {Account, AccountUser, AccountUserBase} from "@/lib/types/accounts.ts";
import {GraphQLResponse} from "@/lib/types/common.ts";
import {DashboardDefinition} from "@/lib/types/website.ts";

export interface BrandInfo {
  isDefault: boolean;
  name: string;
  phone: string;
  statusPageURL: string;
  videoTutorialsURL: string;
  address: string;
  email: string;
  salesEmail: string;
  siteURL: string;
  // Base URL of the status-page consumption SPA. Null for brands outside the rollout.
  consumptionURL: string | null;
  android: string | null;
  ios: string | null;
}

interface Status {
  date: string;
  title: string;
}

export interface CurrentUserContext {
  user?: AccountUser;
  accountAccounts: {
    items: Account[];
  };
  pinnedDashboards: DashboardDefinition[];
  hasUp2Services: boolean;
  brand: BrandInfo;
  status: Status;
}

/* any error on this query leads to auth error and redirect to the main website  */
export const GQL_USER_CONTEXT = gql`
  query User {
    user: currentUser {
      id
      first_name
      last_name
      email
      is_primary
      access_level
      is_api_enabled
      has_enterprise_features
      has_write_access
      has_admin_access
      has_advanced_features
      has_purchase_permission
      whatsnew_read_at
      whatsnew_updated_at
      current_date_format
      current_time_format
      notify_important_updates
      notify_paid_invoices
      ui_settings {
        table_page_size
      }
      account {
        id
        name
        phone
        address
        billing_address
        country
        city
        zipcode
        additional_info
        data_region
        require_two_factor
        type
        plan
        billing_period
        timezone
        free_trial_expires_at
        converted_at
        has_can_purchase_feature
        primary_user_name
        primary_user_email
        limit_check_frequency
        limit_monitoring_group_level
        limit_monitoring_groups
        enable_audit_log
        enable_transaction_ext_checks
        enable_public_link_sharing
        disable_regular_login
        has_sso_configured
        enable_api_access
        limit_private_locations
        is_subaccount
        is_mobile_enabled
        status_pages_use_spa
        feature_flags {
          name
        }
        subscription_type
        inactive_session_timeout
      }
    }
    accountAccounts {
      items {
        id
        name
        is_subaccount
      }
    }
    hasUp2Services
    pinnedDashboards: dashboardDefinitions(pinned: true) {
      id
      name
      ordering
    }
    brand {
      isDefault
      name
      phone
      address
      email
      salesEmail
      statusPageURL
      videoTutorialsURL
      siteURL
      consumptionURL
      android
      ios
    }
    status {
      date
      title
    }
  }
`;

export const GQL_SELECT_SUBACCOUNT = gql`
  mutation SelectSubaccount($id: Int) {
    selectSubaccount(id: $id) {
      success
      errors
    }
  }
`;

export const GQL_ACCOUNTUSERS_QUERY = gql`
  query AccountUsers($subaccount: Int, $sort: String, $limit: Int, $offset: Int) {
    accountUsers(subaccount: $subaccount, sort: $sort, limit: $limit, offset: $offset) {
      items {
        id
        first_name
        last_name
        username
        email
        is_primary
        is_active
        access_level
        must_two_factor
        require_two_factor
        is_api_enabled
        is_mobile_enabled
        notify_paid_invoices
        notify_important_updates
        has_purchase_permission
        has_two_factor_device
        last_login
        access_level
        assigned_subaccounts {
          id
          name
        }
        sso_user_bindings {
          type
          sso_username
        }
      }
      total
    }
    accountAccounts {
      total
      items {
        id
        name
        is_subaccount
      }
    }
    availableContactGrouns: contactGroups(limit: 100, offset: 0) {
      total
      items {
        id
        name
      }
    }
  }
`;

export interface AccountItems {
  items: Account[];
  total: number;
}

export interface AccountUserItems {
  items: AccountUser[];
  total: number;
}

export interface AccountDetails {
  name: string;
  phone: string;
  address: string;
  timezone: string;
  data_region: string;
  require_two_factor: boolean;
  is_mobile_enabled: boolean;
  enable_public_link_sharing: boolean;
  is_subaccount: boolean;
}

export interface AccountUsersQueryResponse {
  accountUsers: AccountUserItems;
  accountAccounts: AccountItems;
  availableContactGroups: ContactGroupItems;
}

export const GQL_ACCOUNTUSER_UPDATE = gql`
  mutation UpdateUser($data: AccountUserUpdateInput!) {
    response: updateAccountUser(data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export type UpdateAccountUserResult = GraphQLResponse<AccountUserBase>;

export const GQL_ACCOUNTDETAILS_UPDATE = gql`
  mutation UpdateAccount($data: AccountDetailsUpdateInput!) {
    response: updateAccountDetails(data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export type UpdateAccountDetailsResult = GraphQLResponse<AccountDetails>;

export const GQL_ACCOUNTUSER_FORM_DEPENDENCY_QUERY = gql`
  query AccountUserDependency {
    accountAccounts {
      total
      items {
        id
        name
        is_subaccount
      }
    }
    availableContactGroups: contactGroups(limit: 100, offset: 0) {
      total
      items {
        id
        name
      }
    }
  }
`;

export interface AccountUserFormDependencyResult {
  accountAccounts: AccountItems;
  availableContactGroups: ContactGroupItems;
}

export const GQL_ACCOUNTUSER_DELETE = gql`
  mutation DeleteAccountUser($id: Int!) {
    response: deleteAccountUser(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_ACCOUNTUSER_MAKE_PRIMARY = gql`
  mutation MakeAccountUserPrimary($id: Int!) {
    response: makePrimary(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_ACCOUNTUSER_RESET_2FA = gql`
  mutation ResetAccountUser2FA($id: Int!) {
    response: resetAccountUser2FA(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_ACCOUNTUSER_RESEND_INVITE = gql`
  mutation ResendAccountUserInvite($id: Int!) {
    response: resendAccountUserInvite(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_ACCOUNTUSER_SET_ACTIVE_STATE = gql`
  mutation SetAccountUserActiveState($id: Int!, $is_active: Boolean!) {
    response: setAccountUserActiveState(id: $id, is_active: $is_active) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_UPDATE = gql`
  mutation UpdateAccountSignupAnalyticsData($data: AccountSignupAnalyticsDataInput!) {
    response: updateAccountSignupAnalyticsData(data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_QUERY = gql`
  query AccountSignupAnalyticsData {
    accountSignupAnalyticsData {
      id
      monitor_my_website
      status_page
      website_monitoring
      page_speed_monitoring
      api_monitoring
      onboarding_wizard_step
      service_id
      service_domain
    }
  }
`;

export const GQL_ACCOUNTUSER_PASSWORD_UPDATE = gql`
  mutation UpdateAccountUserPassword($data: AccountUserPasswordInput!) {
    response: updateAccountUserPassword(data: $data) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_ACCOUNT_SAML_SETTINGS_QUERY = gql`
  query AccountSAMLSettings {
    accountSAMLSettings {
      sp_uuid
      idp_entity_id
      idp_sso_target_url
      idp_certificate
      sp_entity_id
      sp_acs_url
      sp_wayfless_url
      use_sha1_for_signature
      unsigned_authn_requests
    }
    testIDPs {
      items {
        name
        info
        idp_entity_id
        idp_sso_target_url
        idp_certificate
      }
    }
  }
`;

export const GQL_UPDATE_ACCOUNT_SAML_SETTINGS = gql`
  mutation UpdateAccountSAMLSettings($data: SAMLSettingsInput!) {
    response: updateAccountSAMLSettings(data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_REMOVE_ACCOUNT_SAML_SETTINGS = gql`
  mutation RemoveAccountSAMLSettings {
    response: removeAccountSAMLSettings {
      status
    }
  }
`;

export const GQL_UPDATE_CURRENT_USER_UI_SETTINGS = gql`
  mutation UpdateCurrentUserUiSettings($data: JSON!) {
    response: updateCurrentUserUiSettings(data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;
